// src/MockEngine/mockGenerateProject.js
// ------------------------------------------------------------
// MOCK ENGINE — GENERATION (NO SCORING HERE)
// - Loads the JSON library
// - Activates N1/N2 from a typology
// - Instantiates: planning windows, interfaces, requirements, risks
// - Returns a base ProjectInstance (WITHOUT KPIs / alerts)
// ------------------------------------------------------------

import n1Lib from "./n1-systems.json";
import n2Lib from "./n2-subsystems.json";
import ifaceLib from "./interface-patterns.json";
import reqLib from "./requirements-patterns.json";
import riskLib from "./risk-patterns.json";
import typLib from "./typologies.json";

/** -----------------------------
 * Helpers
 * ------------------------------*/

// Strict ISO date parsing (YYYY-MM-DD) using UTC to avoid timezone shifts
function parseISODateUTC(dateStr) {
  if (typeof dateStr !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    throw new Error(`Invalid startDate (expected YYYY-MM-DD): ${dateStr}`);
  }
  const [y, m, d] = dateStr.split("-").map((x) => Number(x));
  return new Date(Date.UTC(y, m - 1, d));
}

// Macro: 30 days per month (coarse). This is intentional for a mock engine.
function addDaysUTC(dateUTC, days) {
  return new Date(dateUTC.getTime() + days * 24 * 60 * 60 * 1000);
}

function monthOffsetToISODate(startDateStr, monthOffset) {
  const startUTC = parseISODateUTC(startDateStr);
  const days = monthOffset * 30; // macro planning approximation
  const dt = addDaysUTC(startUTC, days);
  return dt.toISOString().slice(0, 10);
}

function round1(n) {
  return Number((Math.round(n * 10) / 10).toFixed(1));
}

function uniq(arr) {
  return Array.from(new Set(arr));
}

/** -----------------------------
 * Main generator
 * ------------------------------*/

/**
 * Generates a base ProjectInstance (without KPIs).
 *
 * @param {Object} params
 * @param {string} params.typologyId
 * @param {string} params.projectId
 * @param {string} params.projectName
 * @param {string} params.startDate      YYYY-MM-DD
 * @param {number} params.durationMonths > 0
 *
 * @returns {Object} base ProjectInstance
 */
export function mockGenerateProject({
  typologyId,
  projectId = "PRJ-DEMO-001",
  projectName = "Projet Démo",
  startDate = "2026-01-01",
  durationMonths = 20,
} = {}) {
  if (!typologyId) throw new Error("typologyId is required.");
  if (typeof durationMonths !== "number" || durationMonths <= 0) {
    throw new Error(`durationMonths must be a positive number. Got: ${durationMonths}`);
  }
  // Validate date early
  parseISODateUTC(startDate);

  // --- Lookup typology
  const typology = (typLib.typologies || []).find((t) => t.id === typologyId);
  if (!typology) throw new Error(`Typology not found: ${typologyId}`);

  const complexityFactor = typeof typology.complexityFactor === "number" ? typology.complexityFactor : 1.0;

  // --- Build maps for quick lookups
  const n1ById = Object.fromEntries((n1Lib.systemsN1 || []).map((x) => [x.id, x]));
  const n2ById = Object.fromEntries((n2Lib.subsystemsN2 || []).map((x) => [x.id, x]));

  // --- Activate N1
  const activatedN1 = uniq(typology.activatedN1 || []).filter((id) => !!n1ById[id]);
  if (activatedN1.length === 0) {
    throw new Error(`Typology ${typologyId} activates no valid N1 systems.`);
  }

  // --- Activate N2 (all N2 whose parentN1 is activated)
  const activatedN2 = uniq(
    (n2Lib.subsystemsN2 || [])
      .filter((n2) => activatedN1.includes(n2.parentN1))
      .map((n2) => n2.id)
  );

  // --- Planning windows for N2 (macro, ratios)
  const n2Windows = activatedN2.map((n2Id) => {
    const n2 = n2ById[n2Id];
    if (!n2) return null;

    const startMonth =
      (Number(n2.planStartPct) / 100) * durationMonths * complexityFactor;
    const durationMonth =
      (Number(n2.planDurationPct) / 100) * durationMonths * complexityFactor;

    const startM = round1(startMonth);
    const endM = round1(startMonth + durationMonth);

    return {
      n2Id,
      startMonth: startM,
      endMonth: endM,
      startDate: monthOffsetToISODate(startDate, startMonth),
      endDate: monthOffsetToISODate(startDate, startMonth + durationMonth),
    };
  }).filter(Boolean);

  // --- Interfaces (only when both ends are active)
  const interfaces = (ifaceLib.interfacePatterns || [])
    .filter((p) => activatedN2.includes(p.sourceN2) && activatedN2.includes(p.targetN2))
    .map((p) => ({
      id: p.id,
      sourceN2: p.sourceN2,
      targetN2: p.targetN2,
      type: p.type, // e.g. "TECH"
      criticality: Number(p.criticality ?? 3),
      planningDependency: !!p.planningDependency,
    }));

  // --- Requirements (filter default links to active N2)
  const requirements = (reqLib.requirementPatterns || []).map((r) => ({
    id: r.id,
    description: r.description,
    criticality: Number(r.criticality ?? 3),
    linkedN2: (r.defaultLinkedN2 || []).filter((id) => activatedN2.includes(id)),
  }));

  // --- Risks (keep only those linked to active N2)
  const risks = (riskLib.riskPatterns || [])
    .map((rk) => ({
      id: rk.id,
      description: rk.description,
      severity: Number(rk.severity ?? 3),
      linkedN2: (rk.linkedN2 || []).filter((id) => activatedN2.includes(id)),
    }))
    .filter((rk) => rk.linkedN2.length > 0);

  return {
    project: {
      id: projectId,
      name: projectName,
      typologyId: typology.id,
      startDate,
      durationMonths,
      complexityFactor,
    },

    activated: {
      n1: activatedN1,
      n2: activatedN2,
    },

    planning: {
      n2Windows,
    },

    // Data instances
    requirements,
    risks,
    interfaces,

    // Scoring/KPIs are computed in MockScoring.js
    kpis: null,
  };
}